/**
 * Wire models for the BrightNav member metadata header (MW-10562).
 * Source of truth: EDD 4a (`docs/edd/MW10562-EngineeringDiscovery.md`) — camelCase JSON from
 * `brightnav-api` `GET /v1/prototype/client/metadata` and `GET /v1/prototype/client/household`.
 */

/**
 * Flat header payload for one client, fetched per session `guid` by
 * {@link MemberMetadataHeaderService}. Optional fields render as an em dash (`—`) when null /
 * absent, except `vipStatus` / `doNotContact`, whose badges are hidden entirely.
 */
export interface MemberMetadataHeader {
  guid: string;
  firstName: string;
  lastName: string;
  company: string;
  /** Binary eligibility badge — green when `true`, red when `false`. */
  isEligible: boolean;
  ssnLast4?: string | null;
  /** ISO `YYYY-MM-DD`; age is computed client-side (DOB / Age section). */
  dateOfBirth?: string | null;
  gender?: string | null;
  employmentStatus?: string | null;
  /** Details panel only — never rendered on the primary bar. */
  annualPay?: number | null;
  /** Single formatted mailing address string. */
  address?: string | null;
  phone?: string | null;
  preferredLanguage?: string | null;
  householdCount?: number | null;
  /** Badge renders only when `'active'`; null until the VIP RDS prerequisite lands (EDD OQ-1). */
  vipStatus?: 'active' | null;
  /** Badge renders only when `true`; null until the DNC upstream source is decided (EDD OQ-1). */
  doNotContact?: boolean | null;
  /** Client link icon buttons on the primary bar; icon disabled when `url` is absent. */
  links?: { label: string; url: string }[];
}

/** `GET /v1/prototype/client/metadata` response body (paired with `GenericResponseResult`). */
export interface MemberMetadataHeaderResponse {
  payload: MemberMetadataHeader;
}

/** Schema role from the household roster: primary_client_id → primary; related → household. */
export type HouseholdRole = 'primary' | 'household';

/**
 * One row in the household members modal (Phase 4). The UI highlights the row matching the
 * active session `guid`. Non-primary rows open a stacked member profile dialog (OQ-11);
 * primary is not clickable. Badge uses `householdRole`.
 */
export interface HouseholdMemberRow {
  guid: string;
  firstName: string;
  lastName: string;
  /** Display label from RDS (e.g. "Spouse", "Dependent") or null. */
  relationship: string | null;
  /** ISO `YYYY-MM-DD` or null. */
  dateOfBirth: string | null;
  /** PRIMARY / HOUSEHOLD badge; from API `member_rank`. */
  householdRole: HouseholdRole;
}

/** `GET /v1/prototype/client/household` response body (paired with `GenericResponseResult`). */
export interface HouseholdMembersResponse {
  payload: {
    members: HouseholdMemberRow[];
  };
}
